"use client";

import { Trans } from "react-i18next";
import { appDownloadUrl, type AppDownloadSurface } from "@speakai/common/src/apps/app-links";
import { cn } from "@/lib/utils/cn";
import { isAndroid, isIOS, isNativeMobilePlatform } from "@/lib/utils/ua";

const LINK_CLASS =
  "font-medium text-foreground underline underline-offset-2 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm";

const DEFAULT_PHONE_COPY = {
  i18nKey: "UPLOAD_FILE.PHONE_APP_NOTE_GET_APP",
  defaults: "Recording on the go? <0>Get the app.</0>",
};

interface PhoneAppNoteProps {
  surface: AppDownloadSurface;
  /** Text for a phone browser; `<0>` wraps the link to that phone's store. */
  phoneCopy?: { i18nKey: string; defaults: string };
  className?: string;
}

/** Muted line pointing to the phone apps: one store link on a phone browser, both on a computer, nothing inside the native shell. */
export function PhoneAppNote({ surface, phoneCopy = DEFAULT_PHONE_COPY, className }: PhoneAppNoteProps) {
  if (isNativeMobilePlatform()) return null;

  const iosUrl = appDownloadUrl("ios", surface);
  const androidUrl = appDownloadUrl("android", surface);
  const phoneStoreUrl = isIOS() ? iosUrl : isAndroid() ? androidUrl : null;

  return (
    <p className={cn("text-xs text-muted-foreground", className)} data-testid="phone-app-note">
      {phoneStoreUrl ? (
        <Trans
          i18nKey={phoneCopy.i18nKey}
          defaults={phoneCopy.defaults}
          components={[<a key="0" href={phoneStoreUrl} target="_blank" rel="noopener noreferrer" className={LINK_CLASS} />]}
        />
      ) : (
        <Trans
          i18nKey="UPLOAD_FILE.PHONE_APP_NOTE"
          defaults="Recording on the go? Get the <0>iPhone</0> or <1>Android</1> app."
          components={[
            <a key="0" href={iosUrl} target="_blank" rel="noopener noreferrer" className={LINK_CLASS} />,
            <a key="1" href={androidUrl} target="_blank" rel="noopener noreferrer" className={LINK_CLASS} />,
          ]}
        />
      )}
    </p>
  );
}
